import { useEffect, useState } from 'react';
import {
  ArrowRight,
  ArrowsClockwise,
  ArrowsOut,
  CaretDown,
  ChatsCircle,
  Check,
  CloudArrowUp,
  Cube,
  Export,
  Eye,
  ImageSquare,
  LinkSimple,
  List,
  MapTrifold,
  Ruler,
  ShareNetwork,
  Television,
  UsersThree,
  WarningCircle,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { CompareVisual, type CompareVisualType } from './CompareVisual';

const quickFeatures = [
  {
    icon: Ruler,
    title: '실측 3D 배치',
    copy: '부스 치수와 집기 크기를 mm 단위로 맞춰 현장과 같은 비율로 배치합니다.',
  },
  {
    icon: ImageSquare,
    title: '콘텐츠 미리보기',
    copy: '배너 이미지와 TV 영상을 오브젝트에 연결해 설치 전에 확인합니다.',
  },
  {
    icon: ShareNetwork,
    title: '링크 공유',
    copy: '클라이언트와 팀에게 읽기 전용 링크를 보내 검토를 받습니다.',
  },
  {
    icon: CloudArrowUp,
    title: '클라우드 저장',
    copy: '프로젝트를 저장하고 다음 수정 때 바로 이어서 작업합니다.',
  },
];

const painPoints = [
  {
    icon: MapTrifold,
    title: '평면도 한계',
    copy: '2D 도면으로는 높이, 시선, 동선을 상대방과 공감하기 어렵습니다.',
  },
  {
    icon: WarningCircle,
    title: '현장 수정 비용',
    copy: '설치 후 레이아웃을 바꾸면 인건비와 일정 손실이 바로 발생합니다.',
  },
  {
    icon: ChatsCircle,
    title: '설명의 어려움',
    copy: '“이 위치에 이렇게”를 말과 캡처만으로 전달하기 어렵습니다.',
  },
  {
    icon: ArrowsOut,
    title: '크기 불일치',
    copy: '카탈로그 치수와 실제 부스·집기가 맞는지 현장 전에 확인하기 어렵습니다.',
  },
  {
    icon: Television,
    title: '콘텐츠 사후 확인',
    copy: '배너·TV 영상은 설치가 끝나야 비로소 실제 크기로 볼 수 있습니다.',
  },
  {
    icon: LinkSimple,
    title: '검토 분절',
    copy: 'PPT, 캡처, 파일이 흩어져 피드백이 늦고 버전이 꼬입니다.',
  },
];

const WORKFLOW_PREVIEW_SIZE = { width: 820, height: 480 } as const;

const COMPARE_HERO_IMAGE = {
  file: 'compare-hero.webp',
  alt: 'ExpoSpace 3D 스튜디오에서 부스 레이아웃을 검토하는 화면',
  captureGuide: '뷰포트 중앙. 부스 레이아웃 전체와 주요 오브젝트가 보이게.',
};

const workflowSteps = [
  {
    step: '01',
    title: '공간 설정',
    copy: '부스 형태, 치수, 벽·바닥 마감을 현장 기준으로 정합니다.',
    icon: Ruler,
    previewFile: 'step-01-room.webp',
    previewAlt: 'ExpoSpace에서 부스 치수를 설정하는 화면',
    captureGuide: '스튜디오 뷰포트 중앙. 상단 치수 칩(6×4×2.8m)이 보이게.',
  },
  {
    step: '02',
    title: '오브젝트 배치',
    copy: '80개 이상 실측 집기와 디스플레이를 드래그해 배치합니다.',
    icon: Cube,
    previewFile: 'step-02-place.webp',
    previewAlt: '실측 오브젝트를 3D 공간에 배치한 스튜디오 화면',
    captureGuide: '뷰포트 중앙. 좌측 라이브러리·우측 인스pector는 최소화.',
  },
  {
    step: '03',
    title: '콘텐츠 연결',
    copy: '배너 이미지와 TV 영상을 오브젝트에 연결해 미리봅니다.',
    icon: ImageSquare,
    previewFile: 'step-03-media.webp',
    previewAlt: '오브젝트에 미디어를 연결하는 인스pector 화면',
    captureGuide: 'TV 또는 X배너 선택 후 미디어 패널이 보이게.',
  },
  {
    step: '04',
    title: '시점 검토',
    copy: '입체·평면·정면 뷰로 동선과 시선을 함께 확인합니다.',
    icon: Eye,
    previewFile: 'step-04-view.webp',
    previewAlt: '평면 또는 정면 뷰로 부스 배치를 검토하는 화면',
    captureGuide: '평면 뷰 활성 상태. 하단 뷰 전환 버튼 포함.',
  },
  {
    step: '05',
    title: '링크 공유',
    copy: '팀과 클라이언트에게 읽기 전용 링크로 검토를 요청합니다.',
    icon: Export,
    previewFile: 'step-05-share.webp',
    previewAlt: '공유 링크를 생성하는 모달 화면',
    captureGuide: '공유 모달 전체 또는 생성된 링크 영역.',
  },
  {
    step: '06',
    title: '클라우드 저장',
    copy: '프로젝트를 저장하고 수정이 필요할 때 바로 이어 작업합니다.',
    icon: CloudArrowUp,
    previewFile: 'step-06-cloud.webp',
    previewAlt: '클라우드 저장이 완료된 스튜디오 헤더',
    captureGuide: '헤더의 클라우드 저장 버튼·저장 상태가 보이게.',
  },
];

const comparisons: Array<{
  task: string;
  before: string;
  after: string;
  note: string;
  hook: string;
  icon: Icon;
  visual: CompareVisualType;
}> = [
  {
    task: '레이아웃 검토',
    before: '도면과 상상에 의존',
    after: '3D 실측 배치로 동선 확인',
    note: '높이와 시선을 같은 화면에서 검토합니다.',
    hook: '“이 위치에서 뭐가 보이지?”라는 질문을 입체·평면·정면 뷰로 바로 답합니다. 설치 전에 동선과 시선 높이를 팀과 클라이언트가 같은 기준으로 맞춥니다.',
    icon: Eye,
    visual: 'layout',
  },
  {
    task: '크기 확인',
    before: '카탈로그 치수 추측',
    after: 'mm 단위로 배치',
    note: '집기와 부스 비율을 현장 전에 맞춥니다.',
    hook: '카탈로그의 “대략” 치수 대신 mm 단위로 배치해, TV·테이블·통로 폭이 현장에서 어긋나는 일을 줄입니다.',
    icon: Ruler,
    visual: 'size',
  },
  {
    task: '클라이언트 공유',
    before: 'PPT와 캡처 전달',
    after: '링크 하나로 공유',
    note: '읽기 전용 링크로 검토 피드백을 받습니다.',
    hook: 'PPT·캡처·PDF를 반복 전달할 필요 없이, 링크 하나로 3D 공간을 공유합니다. 클라이언트는 앱 설치 없이 브라우저에서 바로 봅니다.',
    icon: ShareNetwork,
    visual: 'share',
  },
  {
    task: '콘텐츠 확인',
    before: '현장 설치 이후',
    after: '배치 전 미리보기',
    note: '배너와 TV 영상을 오브젝트에 연결해 봅니다.',
    hook: '현장 설치 후에야 알게 되는 실제 크기감을, 배치 단계에서 이미지·영상 연결로 미리 확인합니다.',
    icon: Television,
    visual: 'content',
  },
  {
    task: '수정 반복',
    before: '현장 재설치',
    after: '브라우저에서 즉시 수정',
    note: '레이아웃 변경을 설치 전에 반복합니다.',
    hook: '“조금만 옮겨주세요”가 현장 재설치로 이어지는 비용을, 브라우저에서 드래그 한 번으로 대체합니다.',
    icon: ArrowsClockwise,
    visual: 'revision',
  },
  {
    task: '협업',
    before: '파일이 흩어짐',
    after: '클라우드 프로젝트',
    note: '팀이 같은 프로젝트를 이어서 작업합니다.',
    hook: 'final_v3.pptx가 어디 있는지 찾는 대신, 클라우드 프로젝트 하나로 기획·디자인·시공팀이 같은 3D 공간을 이어갑니다.',
    icon: UsersThree,
    visual: 'collab',
  },
];

const audiences = [
  '전시·행사 기획자',
  '부스 시공·디자인팀',
  '브랜드·마케팅 담당자',
  '공간 디자인 에이전시',
];

const navPages = [
  {
    label: 'Docs',
    href: '/docs',
    description: 'ExpoSpace 사용 가이드와 기능 설명을 확인합니다.',
  },
  {
    label: '이용약관',
    href: '/terms',
    description: '서비스 이용약관과 운영 정책을 확인합니다.',
  },
  {
    label: '기술문의',
    href: 'mailto:help@thinkingfriends.co.kr?subject=ExpoSpace%20기술문의',
    description: '도입·기술 지원 문의를 보냅니다.',
  },
];

const capabilityItems = [
  { strong: '실측 사이즈', span: '크기 왜곡 없이 배치' },
  { strong: '로컬 미디어', span: '이미지·영상을 안전하게 미리보기' },
  { strong: '공간 커스텀', span: '다각형 바닥과 벽별 마감 설정' },
  { strong: '3D 모델 등록', span: 'GLB·GLTF 오브젝트 확장' },
  { strong: '오브젝트 스택', span: '테이블 위 TV 등 3단 배치' },
  { strong: '대화 모드', span: '시연·소개 시나리오 연출' },
];

export function LandingPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [compareHeroLoaded, setCompareHeroLoaded] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setPreviewLoaded(false);
  }, [activeStep]);

  return (
    <main className="landing-page">
      <a className="landing-skip" href="#main-content">본문으로 건너뛰기</a>

      <nav className="landing-nav" aria-label="주요 메뉴">
        <a className="landing-brand" href="/" aria-label="ExpoSpace 홈">
          <span className="brand-mark" aria-hidden="true"><span /></span>
          <strong>ExpoSpace</strong>
        </a>
        <div className="landing-nav-links">
          {navPages.map(({ label, href, description }) => (
            <div key={label} className="landing-nav-item">
              <a href={href} className="landing-nav-trigger">
                {label}
                <CaretDown size={12} weight="bold" aria-hidden="true" />
              </a>
              <div className="landing-dropdown landing-dropdown-wide">
                <div className="landing-dropdown-panel">
                  <a href={href} className="landing-dropdown-entry">
                    <strong>{label}</strong>
                    <span>{description}</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="landing-nav-actions">
          <button
            type="button"
            className="landing-nav-menu"
            aria-expanded={mobileNavOpen}
            aria-controls="landing-nav-mobile-panel"
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <List size={20} weight="bold" aria-hidden="true" />
            <span className="sr-only">메뉴</span>
          </button>
          <a className="landing-text-link" href="/studio?login=1">로그인</a>
          <a className="landing-button" href="/studio">바로 내 공간 만들기 <ArrowRight size={18} /></a>
        </div>
        <div
          id="landing-nav-mobile-panel"
          className={`landing-nav-mobile${mobileNavOpen ? ' is-open' : ''}`}
          hidden={!mobileNavOpen}
        >
          <div className="landing-dropdown-panel">
            {navPages.map(({ label, href, description }) => (
              <a
                key={label}
                href={href}
                className="landing-dropdown-entry"
                onClick={() => setMobileNavOpen(false)}
              >
                <strong>{label}</strong>
                <span>{description}</span>
              </a>
            ))}
          </div>
        </div>
      </nav>

      <section className="landing-hero" id="main-content" aria-label="서비스 소개">
        <div className="landing-hero-copy">
          <span className="landing-eyebrow">3D EXHIBITION PLANNER</span>
          <h1>설치 전에,<br />공간을 먼저 봅니다.</h1>
          <p>실측 오브젝트를 배치하고, 콘텐츠를 연결하고, 링크로 검토까지 한 작업 공간에서 진행하세요.</p>
          <div className="landing-hero-actions">
            <a className="landing-button" href="/studio">바로 내 공간 만들기 <ArrowRight size={18} /></a>
            <a className="landing-secondary" href="#features">기능 보기</a>
          </div>
        </div>
        <figure className="landing-hero-visual">
          <img src="/assets/landing-hero.webp" alt="계획 도면에서 완성된 전시 부스로 이어지는 3D 공간" width="1800" height="1200" fetchPriority="high" />
        </figure>
      </section>

      <section className="landing-pillars" aria-label="핵심 기능">
        <header>
          <h2>현장 크기 그대로, 설치 전에 검증하세요</h2>
          <p>공간 설정부터 공유까지 결과가 남는 4가지 핵심 기능입니다.</p>
        </header>
        <div className="landing-pillars-grid">
          {quickFeatures.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <span className="landing-icon-badge" aria-hidden="true"><Icon size={22} weight="duotone" /></span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-problems" id="problems" aria-label="현장에서 반복되는 문제">
        <header>
          <span className="landing-section-label">WHY IT HURTS</span>
          <h2>부스 설계를 시작했는데, 왜 매번 현장에서 문제가 생길까요?</h2>
          <p>많은 팀이 도면과 PPT로 검토하지만, 실제 설치 현장에서는 같은 불일치가 반복됩니다.</p>
        </header>
        <div className="landing-problems-grid">
          {painPoints.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <Icon size={24} weight="duotone" aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-capabilities" id="features" aria-label="상세 기능">
        <figure className="landing-capabilities-image">
          <img src="/assets/auth-booth.webp" alt="TV와 X배너, 안내 카운터를 배치한 전시 부스" width="900" height="1350" loading="lazy" />
        </figure>
        <div className="landing-capabilities-copy">
          <span className="landing-section-label">CORE FEATURES</span>
          <h2>판단에 필요한 디테일만 정확하게</h2>
          <p>크기와 위치는 현장 기준으로 고정하고, 표현과 콘텐츠는 자유롭게 바꿀 수 있습니다.</p>
          <ul>
            {capabilityItems.map(({ strong, span }) => (
              <li key={strong}>
                <Check size={18} weight="bold" aria-hidden="true" />
                <span><strong>{strong}</strong>{span}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="landing-workflow" id="workflow" aria-label="ExpoSpace 제작 흐름">
        <header>
          <span className="landing-section-label">WORKFLOW</span>
          <h2>ExpoSpace 제작 흐름</h2>
          <p>공간 설정에서 링크 공유까지, 설치 전 검토가 남는 6단계입니다.</p>
        </header>
        <div className="landing-workflow-shell">
          <ol className="landing-workflow-steps">
            {workflowSteps.map(({ step, title, copy, icon: Icon }, index) => (
              <li key={step}>
                <button
                  type="button"
                  className={index === activeStep ? 'is-active' : ''}
                  aria-pressed={index === activeStep}
                  onClick={() => setActiveStep(index)}
                >
                  <span className="landing-workflow-step-no">{step}</span>
                  <span className="landing-workflow-step-copy">
                    <strong>{title}</strong>
                    <small>{copy}</small>
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <aside className="landing-workflow-preview" aria-live="polite">
            {(() => {
              const { step, title, copy, previewFile, previewAlt, captureGuide } = workflowSteps[activeStep];
              const previewSrc = `/assets/workflow/${previewFile}`;
              return (
                <>
                  <figure className={`landing-workflow-preview-figure${previewLoaded ? ' is-loaded' : ''}`}>
                    <div className="landing-workflow-preview-slot" aria-hidden={previewLoaded}>
                      <span className="landing-workflow-preview-size">
                        {WORKFLOW_PREVIEW_SIZE.width} × {WORKFLOW_PREVIEW_SIZE.height}
                      </span>
                      <p>{captureGuide}</p>
                      <code>{previewSrc}</code>
                    </div>
                    <img
                      key={previewSrc}
                      src={previewSrc}
                      alt={previewAlt}
                      loading="lazy"
                      decoding="async"
                      onLoad={() => setPreviewLoaded(true)}
                      onError={() => setPreviewLoaded(false)}
                    />
                  </figure>
                  <div className="landing-workflow-preview-copy">
                    <span className="landing-workflow-preview-label">STEP {step}</span>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </div>
                </>
              );
            })()}
          </aside>
        </div>
      </section>

      <section className="landing-compare" aria-label="도입 전후 비교">
        <header>
          <h2>부스 검토는 파일이 아니라 현장 손실로 증명됩니다</h2>
          <p>설치 전 3D 검토로 바꾸면, 수정에 쓰이던 시간이 기획과 실행으로 돌아갑니다.</p>
        </header>

        <div className="landing-compare-hero">
          <figure className={`landing-compare-hero-visual${compareHeroLoaded ? ' is-loaded' : ''}`}>
            <div className="landing-workflow-preview-slot" aria-hidden={compareHeroLoaded}>
              <span className="landing-workflow-preview-size">
                {WORKFLOW_PREVIEW_SIZE.width} × {WORKFLOW_PREVIEW_SIZE.height}
              </span>
              <p>{COMPARE_HERO_IMAGE.captureGuide}</p>
              <code>{`/assets/landing/${COMPARE_HERO_IMAGE.file}`}</code>
            </div>
            <img
              src={`/assets/landing/${COMPARE_HERO_IMAGE.file}`}
              alt={COMPARE_HERO_IMAGE.alt}
              width={WORKFLOW_PREVIEW_SIZE.width}
              height={WORKFLOW_PREVIEW_SIZE.height}
              loading="lazy"
              decoding="async"
              onLoad={() => setCompareHeroLoaded(true)}
              onError={() => setCompareHeroLoaded(false)}
            />
          </figure>
          <div className="landing-compare-hero-copy">
            <h3>말로 설명하는 레이아웃 검토는 여기서 끝납니다</h3>
            <p>도면과 캡처를 넘기는 대신, 실측 오브젝트가 놓인 3D 공간을 팀과 클라이언트가 같은 링크로 봅니다.</p>
            <ul>
              <li><Check size={16} weight="bold" aria-hidden="true" /><span>동선과 시선을 입체 뷰로 확인</span></li>
              <li><Check size={16} weight="bold" aria-hidden="true" /><span>배너·TV 콘텐츠를 설치 전에 연결</span></li>
              <li><Check size={16} weight="bold" aria-hidden="true" /><span>수정은 브라우저에서, 현장 비용 없이</span></li>
            </ul>
          </div>
        </div>

        <div className="landing-compare-board" role="table" aria-label="기존 방식과 ExpoSpace 비교">
          <div className="landing-compare-board-head" role="row">
            <span role="columnheader">작업</span>
            <span role="columnheader">기존 방식</span>
            <span role="columnheader">ExpoSpace</span>
          </div>
          {comparisons.map(({ task, before, after, note, hook, icon: TaskIcon, visual }) => (
            <article key={task} className="landing-compare-row" role="row">
              <div className="landing-compare-task" role="cell">
                <div className="landing-compare-task-head">
                  <span className="landing-compare-task-icon" aria-hidden="true">
                    <TaskIcon size={20} weight="duotone" />
                  </span>
                  <strong>{task}</strong>
                </div>
                <div className="landing-compare-task-copy">
                  <p className="landing-compare-task-lead">{note}</p>
                  <p className="landing-compare-task-hook">{hook}</p>
                </div>
              </div>
              <div className="landing-compare-before" role="cell">
                <span className="landing-compare-tag">기존</span>
                <figure className="landing-compare-visual" aria-label={before}>
                  <CompareVisual type={visual} side="before" />
                </figure>
                <p>{before}</p>
              </div>
              <div className="landing-compare-after" role="cell">
                <span className="landing-compare-tag">ExpoSpace</span>
                <figure className="landing-compare-visual" aria-label={after}>
                  <CompareVisual type={visual} side="after" />
                </figure>
                <p>{after}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-audience" aria-label="대상 사용자">
        <div className="landing-audience-panel">
          <div className="landing-audience-copy">
            <span className="landing-section-label">FOR TEAMS</span>
            <h2>현장을 아는 팀을 위한 3D 작업 공간</h2>
            <p>전시 부스와 행사 공간을 기획·시공·검토하는 팀이 같은 화면으로 결과물을 남깁니다.</p>
          </div>
          <ul className="landing-audience-list">
            {audiences.map((item) => (
              <li key={item}><UsersThree size={18} weight="duotone" aria-hidden="true" />{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="landing-final-cta" aria-label="시작 안내">
        <div>
          <span className="landing-section-label">GET STARTED</span>
          <h2>
            설치 전에, 공간에서
            <br />
            먼저 확인하세요.
          </h2>
          <p>브라우저에서 바로 시작하고, 팀과 링크로 검토를 이어가세요.</p>
        </div>
        <a className="landing-button" href="/studio">편집기 열기 <ArrowRight size={18} /></a>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-brand">
          <a href="https://www.thinkingfriends.co.kr/" target="_blank" rel="noreferrer" aria-label="띵킹프렌즈 홈페이지 새 창에서 열기">
            <span>THINKING</span>
            <strong>FRIENDS</strong>
          </a>
          <p>ExpoSpace는 띵킹프렌즈가 만들고 운영합니다.</p>
        </div>

        <div className="landing-footer-company">
          <p><strong>주식회사 띵킹프렌즈</strong></p>
          <p>대표 김강민 <i aria-hidden="true" /> 개인정보책임자 김상웅</p>
          <p>사업자등록번호 866-88-01498 <i aria-hidden="true" /> 통신판매업신고 제2022-부산해운대-0028호</p>
          <address>
            부산광역시 해운대구 센텀중앙로 55, 906호<br />
            서울특별시 강남구 역삼로65길 15, 501호 <span>띵킹프렌즈 기업부설연구소</span>
          </address>
          <p className="landing-footer-contact">
            <a href="tel:07075436341">070-7543-6341</a>
            <a href="mailto:help@thinkingfriends.co.kr">help@thinkingfriends.co.kr</a>
          </p>
        </div>

        <nav className="landing-footer-nav" aria-label="띵킹프렌즈 관련 링크">
          <a href="https://www.thinkingfriends.co.kr/" target="_blank" rel="noreferrer">Home</a>
          <a href="https://www.thinkingfriends.co.kr/itprojects" target="_blank" rel="noreferrer">Projects</a>
          <a href="https://www.thinkingfriends.co.kr/blog" target="_blank" rel="noreferrer">Blog</a>
          <a href="https://www.thinkingfriends.co.kr/inquary" target="_blank" rel="noreferrer">문의하기</a>
        </nav>

        <p className="landing-footer-copyright">© {new Date().getFullYear()} Thinking Friends Co., Ltd. All rights reserved.</p>
      </footer>
    </main>
  );
}
